import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  SplitVertical,
  UserIcon,
} from "@/components/icons";
import {
  Button,
  Skeleton,
  confirm,
  toast,
} from "@/components/kit";
import { cn } from "@/lib/utils";
import type { CustomerDTO } from "@/modules/customers/domain/types";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import {
  useCustomerMergePreviewQuery,
  useCustomerQuery,
  useMergeCustomers,
} from "../api/queries";
import { CustomerSelectPanel } from "../ui/CustomerSelectPanel";

type MergeSearch = {
  sourceId?: string;
  targetId?: string;
};

function CountRow({
  label,
  value,
}: {
  label: string;
  value: number;
}) {
  return (
    <div className="flex items-center justify-between gap-3 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium tabular-nums">{value}</span>
    </div>
  );
}

export function CustomerMergePage() {
  const nav = useNavigate();
  const search = useSearch({ strict: false }) as MergeSearch;
  const canWrite = useActionPermission("orders:write");
  const mergeCustomers = useMergeCustomers();

  const [source, setSource] = useState<CustomerDTO | null>(null);
  const [target, setTarget] = useState<CustomerDTO | null>(null);

  const prefilledSource = useCustomerQuery(search.sourceId ?? "");
  const prefilledTarget = useCustomerQuery(search.targetId ?? "");

  useEffect(() => {
    if (search.sourceId && prefilledSource.data && !source) {
      setSource(prefilledSource.data);
    }
  }, [search.sourceId, prefilledSource.data, source]);

  useEffect(() => {
    if (search.targetId && prefilledTarget.data && !target) {
      setTarget(prefilledTarget.data);
    }
  }, [search.targetId, prefilledTarget.data, target]);

  const previewInput = useMemo(() => {
    if (!source || !target || source.id === target.id) return null;
    return { sourceId: source.id, targetId: target.id };
  }, [source, target]);

  const preview = useCustomerMergePreviewQuery(previewInput, canWrite);

  function swapCustomers() {
    setSource(target);
    setTarget(source);
  }

  async function handleMerge() {
    if (!source || !target) {
      toast.error("ເລືອກລູກຄ້າຕົ້ນທາງ ແລະ ປາຍທາງ");
      return;
    }
    if (source.id === target.id) {
      toast.error("ບໍ່ສາມາດ merge ລູກຄ້າຄົນດຽວກັນ");
      return;
    }

    const counts = preview.data?.sourceCounts;
    const ok = await confirm({
      title: "ຢືນຢັນການ merge ລູກຄ້າ?",
      description: [
        `ຕົ້ນທາງ: ${source.name} (ຈະຖືກລຶບ)`,
        `ປາຍທາງ: ${target.name} (ຮັບຂໍ້ມູນທັງໝົດ)`,
        counts
          ? `ຍ້າຍ ${counts.orders} ອໍເດີ້ · ${counts.customJobs} ງານສັ່ງເຮັດ · ${counts.restockWaitlist} waitlist`
          : "ຂໍ້ມູນທັງໝົດຈະຖືກຍ້າຍໄປລູກຄ້າປາຍທາງ",
      ].join("\n"),
      actionText: "Merge",
      ActionProps: { variant: "destructive" },
    });
    if (!ok) return;

    try {
      const result = await mergeCustomers.mutateAsync({
        sourceId: source.id,
        targetId: target.id,
      });
      toast.success(
        `Merge ສຳເລັດ · ຍ້າຍ ${result.moved.orders} ອໍເດີ້, ${result.moved.customJobs} ງານ, ${result.moved.restockWaitlist} waitlist`,
      );
      nav({
        to: "/app/customers/$id",
        params: { id: result.targetId },
      });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Merge ບໍ່ສຳເລັດ");
    }
  }

  if (!canWrite) {
    return (
      <>
        <Header />
        <Main>
          <div className="brand-card p-8 text-center text-muted-foreground">
            ບໍ່ມີສິດ merge ລູກຄ້າ
          </div>
        </Main>
      </>
    );
  }

  const sameCustomer = source && target && source.id === target.id;

  return (
    <>
      <Header />
      <Main className="mx-auto flex w-full max-w-4xl flex-col gap-6 pb-24">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex flex-col gap-1">
            <h2 className="font-display text-2xl text-foreground sm:text-3xl">
              Merge ລູກຄ້າ
            </h2>
            <p className="text-muted-foreground text-sm">
              ເລືອກລູກຄ້າຕົ້ນທາງ (ຈະຖືກລຶບ) ແລະ ປາຍທາງ (ຮັບຂໍ້ມູນທັງໝົດ)
            </p>
            <div className="mt-2 h-0.5 w-16 rounded-full bg-secondary/70" />
          </div>
          <Button
            variant="outline"
            className="rounded-full border-secondary/40"
            onClick={() => nav({ to: "/app/customers" })}
          >
            <ArrowLeftIcon data-icon="inline-start" />
            ກັບ
          </Button>
        </div>

        <div className="grid gap-4 lg:grid-cols-[1fr_auto_1fr] lg:items-start">
          <CustomerSelectPanel
            label="ລູກຄ້າຕົ້ນທາງ"
            description="ບັນທຶກຊ້ຳ / ລູກຄ້າທີ່ຕ້ອງການລຶບ — ຂໍ້ມູນຈະຖືກຍ້າຍໄປປາຍທາງ"
            selected={source}
            onSelect={setSource}
            onClear={() => setSource(null)}
            excludeId={target?.id}
            tone="source"
          />

          <div className="flex justify-center lg:pt-16">
            <Button
              type="button"
              variant="outline"
              size="icon"
              className="rounded-full"
              disabled={!source && !target}
              onClick={swapCustomers}
              title="ສະລັບຕົ້ນທາງ/ປາຍທາງ"
            >
              <SplitVertical className="size-4" />
            </Button>
          </div>

          <CustomerSelectPanel
            label="ລູກຄ້າປາຍທາງ"
            description="ລູກຄ້າຫຼັກທີ່ຈະເຫຼືອ — ຮັບອໍເດີ້, waitlist ແລະ ງານສັ່ງເຮັດ"
            selected={target}
            onSelect={setTarget}
            onClear={() => setTarget(null)}
            excludeId={source?.id}
            tone="target"
          />
        </div>

        {sameCustomer ? (
          <div className="rounded-2xl border border-destructive/30 bg-destructive/5 px-4 py-3 text-destructive text-sm">
            ຕົ້ນທາງ ແລະ ປາຍທາງຕ້ອງເປັນຄົນລະຄົນ
          </div>
        ) : null}

        {previewInput ? (
          <section className="brand-card flex flex-col gap-4 p-4 sm:p-5">
            <div className="flex items-start gap-3">
              <div className="flex size-10 shrink-0 items-center justify-center rounded-2xl bg-secondary/12 text-secondary">
                <UserIcon className="size-4" />
              </div>
              <div>
                <p className="font-medium">ສະຫຼຸບກ່ອນ merge</p>
                <p className="mt-1 text-muted-foreground text-sm">
                  ຂໍ້ມູນຈາກ{" "}
                  <span className="font-medium text-foreground">
                    {source?.name}
                  </span>{" "}
                  <ArrowRightIcon className="mx-1 inline size-3.5" />{" "}
                  <span className="font-medium text-foreground">
                    {target?.name}
                  </span>
                </p>
              </div>
            </div>

            {preview.isLoading ? (
              <Skeleton className="h-24 rounded-xl" />
            ) : preview.data ? (
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="rounded-xl bg-destructive/5 px-4 py-3">
                  <p className="mb-2 font-medium text-sm">ຈະຍ້າຍຈາກຕົ້ນທາງ</p>
                  <div className="flex flex-col gap-1.5">
                    <CountRow
                      label="ອໍເດີ້"
                      value={preview.data.sourceCounts.orders}
                    />
                    <CountRow
                      label="ງານສັ່ງເຮັດ"
                      value={preview.data.sourceCounts.customJobs}
                    />
                    <CountRow
                      label="Waitlist"
                      value={preview.data.sourceCounts.restockWaitlist}
                    />
                  </div>
                </div>
                <div className="rounded-xl bg-leaf/5 px-4 py-3">
                  <p className="mb-2 font-medium text-sm">ປາຍທາງປັດຈຸບັນ</p>
                  <div className="flex flex-col gap-1.5">
                    <CountRow
                      label="ອໍເດີ້"
                      value={preview.data.targetCounts.orders}
                    />
                    <CountRow
                      label="ງານສັ່ງເຮັດ"
                      value={preview.data.targetCounts.customJobs}
                    />
                    <CountRow
                      label="Waitlist"
                      value={preview.data.targetCounts.restockWaitlist}
                    />
                  </div>
                </div>
              </div>
            ) : null}

            <p className="text-muted-foreground text-xs">
              ເບີ/ທີ່ຢູ່/ໝາຍເຫດທີ່ປາຍທາງຍັງວ່າງຈະຖືກເຕີມຈາກຕົ້ນທາງ · ລູກຄ້າຕົ້ນທາງຈະຖືກລຶບຖາວອນ
            </p>
          </section>
        ) : null}

        <div className="sticky bottom-4 z-10">
          <Button
            className={cn(
              "w-full rounded-full",
              sameCustomer && "pointer-events-none opacity-50",
            )}
            disabled={
              !source ||
              !target ||
              sameCustomer ||
              mergeCustomers.isPending ||
              preview.isLoading
            }
            onClick={handleMerge}
          >
            {mergeCustomers.isPending ? "ກຳລັງ merge..." : "Merge ລູກຄ້າ"}
          </Button>
        </div>
      </Main>
    </>
  );
}
